import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { moviesAPI, smartRatingsAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const AdminMovies = () => {
  const navigate = useNavigate();
  const [movies, setMovies] = useState([]);
  const [activeTab, setActiveTab] = useState('now_playing'); // 'now_playing', 'upcoming', or 'archived'
  const [searchTerm, setSearchTerm] = useState('');
  const [archiveSearchTerm, setArchiveSearchTerm] = useState('');
  const [smartRatingOptions, setSmartRatingOptions] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMovie, setEditingMovie] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    original_title: '',
    poster: '',
    header_image: '',
    show_in_slider: false,
    trailer: '',
    synopsis: '',
    duration: '',
    genres: '',
    formats: [],
    rating: 'Genel İzleyici Kitlesi İçin Uygundur',
    smartRatings: [],
    release_date: '',
    director: '',
    cast: '',
    second_release_date: '',
    distributor: '',
    producer: '',
    country: '',
    importer: '',
    status: 'now_playing',
  });
  const { toast } = useToast();

  useEffect(() => {
    loadMovies();
    loadSmartRatings();
  }, []);

  const loadSmartRatings = async () => {
    try {
      const response = await smartRatingsAPI.getAll();
      setSmartRatingOptions(response.data);
    } catch (error) {
      console.error('Error loading smart ratings:', error);
    }
  };

  // Format date for display (ISO date to Turkish format)
  const formatDateForDisplay = (isoDate) => {
    if (!isoDate) return '';
    try {
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return isoDate;
      
      const monthNames = [
        'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
        'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
      ];
      
      const day = date.getDate();
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      
      return `${day} ${month} ${year}`;
    } catch {
      return isoDate;
    }
  };

  // Convert Turkish date to ISO format for input
  const convertToISODate = (turkishDate) => {
    if (!turkishDate) return '';
    if (turkishDate.includes('-')) return turkishDate; // Already ISO format
    
    try {
      const turkishToEnglish = {
        'Ocak': 'January', 'Şubat': 'February', 'Mart': 'March',
        'Nisan': 'April', 'Mayıs': 'May', 'Haziran': 'June',
        'Temmuz': 'July', 'Ağustos': 'August', 'Eylül': 'September',
        'Ekim': 'October', 'Kasım': 'November', 'Aralık': 'December'
      };
      
      let englishDate = turkishDate;
      Object.keys(turkishToEnglish).forEach(turkishMonth => {
        englishDate = englishDate.replace(turkishMonth, turkishToEnglish[turkishMonth]);
      });
      
      const date = new Date(englishDate);
      if (isNaN(date.getTime())) return '';
      
      return date.toISOString().split('T')[0]; // Return YYYY-MM-DD
    } catch {
      return '';
    }
  };

  // Filter movies by active tab and search term
  const getFilteredMovies = () => {
    let filtered = movies.filter(movie => movie.status === activeTab);
    
    const currentSearchTerm = activeTab === 'archived' ? archiveSearchTerm : searchTerm;
    
    if (currentSearchTerm.trim()) {
      filtered = filtered.filter(movie => 
        movie.title.toLowerCase().includes(currentSearchTerm.toLowerCase()) ||
        (movie.original_title && movie.original_title.toLowerCase().includes(currentSearchTerm.toLowerCase())) ||
        (movie.director && movie.director.toLowerCase().includes(currentSearchTerm.toLowerCase())) ||
        (movie.genres && movie.genres.some(genre => 
          genre.toLowerCase().includes(currentSearchTerm.toLowerCase())
        ))
      );
    }
    
    return filtered;
  };

  const loadMovies = async () => {
    try {
      const response = await moviesAPI.getAll();
      setMovies(response.data);
    } catch (error) {
      toast({
        title: 'Hata',
        description: 'Filmler yüklenemedi',
        variant: 'destructive',
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        duration: parseInt(formData.duration),
        genres: formData.genres.split(',').map(g => g.trim()),
        release_date: formatDateForDisplay(formData.release_date), // Convert to Turkish format
        second_release_date: formData.second_release_date ? formatDateForDisplay(formData.second_release_date) : '',
      };

      if (editingMovie) {
        await moviesAPI.update(editingMovie._id, data);
        toast({ title: 'Başarılı', description: 'Film güncellendi' });
      } else {
        await moviesAPI.create(data);
        toast({ title: 'Başarılı', description: 'Film eklendi' });
      }

      setIsDialogOpen(false);
      resetForm();
      loadMovies();
    } catch (error) {
      toast({
        title: 'Hata',
        description: error.response?.data?.detail || 'Film kaydedilemedi',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (movie) => {
    navigate(`/admin/movies/edit/${movie._id}`);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu filmi silmek istediğinizden emin misiniz?')) {
      try {
        await moviesAPI.delete(id);
        toast({ title: 'Başarılı', description: 'Film silindi' });
        loadMovies();
      } catch (error) {
        toast({
          title: 'Hata',
          description: 'Film silinemedi',
          variant: 'destructive',
        });
      }
    }
  };

  const resetForm = () => {
    setEditingMovie(null);
    setFormData({
      title: '',
      original_title: '',
      poster: '',
      header_image: '',
      show_in_slider: false,
      trailer: '',
      synopsis: '',
      duration: '',
      genres: '',
      formats: [],
      rating: 'Genel İzleyici Kitlesi İçin Uygundur',
      smartRatings: [],
      release_date: '',
      director: '',
      cast: '',
      second_release_date: '',
      distributor: '',
      producer: '',
      country: '',
      importer: '',
      status: 'now_playing',
    });
  };

  return (
    <AdminLayout title="Film Yönetimi">
      <div>
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-white">Film Yönetimi</h1>
          <Button
            onClick={() => navigate('/admin/movies/new')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Yeni Film
          </Button>
        </div>

        <div className="space-y-6">
          <div className="flex space-x-1 bg-gray-800 p-1 rounded-lg">
            <button
              onClick={() => setActiveTab('now_playing')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                activeTab === 'now_playing'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              Vizyondakiler ({movies.filter(m => m.status === 'now_playing').length})
            </button>
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                activeTab === 'upcoming'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              Yakında ({movies.filter(m => m.status === 'upcoming').length})
            </button>
            <button
              onClick={() => setActiveTab('archived')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                activeTab === 'archived'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              Arşiv ({movies.filter(m => m.status === 'archived').length})
            </button>
          </div>

          {/* Search Bar */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Input
                type="text"
                placeholder={activeTab === 'archived' ? "Arşivde ara..." : "Film ara... (ad, yönetmen, tür)"}
                value={activeTab === 'archived' ? archiveSearchTerm : searchTerm}
                onChange={(e) => activeTab === 'archived' ? setArchiveSearchTerm(e.target.value) : setSearchTerm(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white pl-10"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            {((activeTab === 'archived' && archiveSearchTerm) || (activeTab !== 'archived' && searchTerm)) && (
              <div className="text-sm text-gray-400">
                {getFilteredMovies().length} sonuç bulundu
              </div>
            )}
          </div>

          {/* Archived Movies - Table View */}
          {activeTab === 'archived' ? (
            <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Film
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Türler
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Vizyon Tarihi
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Süre
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {getFilteredMovies().length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                          {archiveSearchTerm ? 'Arşivde sonuç bulunamadı' : 'Arşivde film yok'}
                        </td>
                      </tr>
                    ) : (
                      getFilteredMovies().map((movie) => (
                        <tr key={movie._id} className="hover:bg-gray-800/50 transition-colors">
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              <img
                                src={movie.poster}
                                alt={movie.title}
                                className="w-12 h-16 object-cover rounded"
                              />
                              <div>
                                <div className="text-white font-medium">{movie.title}</div>
                                {movie.original_title && (
                                  <div className="text-gray-500 text-sm">{movie.original_title}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex flex-wrap gap-1">
                              {movie.genres?.slice(0, 2).map((genre, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-1 bg-blue-500/10 text-blue-400 text-xs rounded"
                                >
                                  {genre}
                                </span>
                              ))}
                              {movie.genres?.length > 2 && (
                                <span className="px-2 py-1 bg-gray-700 text-gray-400 text-xs rounded">
                                  +{movie.genres.length - 2}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-gray-300 text-sm">
                            {movie.release_date || '-'}
                          </td>
                          <td className="px-4 py-4 text-gray-300 text-sm">
                            {movie.duration ? `${movie.duration} dk` : '-'}
                          </td>
                          <td className="px-4 py-4 text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEdit(movie)}
                                className="border-gray-700 text-gray-300 hover:bg-gray-800"
                              >
                                <Edit size={14} className="mr-1" />
                                Düzenle
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDelete(movie._id)}
                                className="border-red-700 text-red-500 hover:bg-red-900/20"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* Active and Upcoming Movies - Card View */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getFilteredMovies().map((movie) => (
                <Card key={movie._id} className="bg-gray-900 border-gray-800 overflow-hidden">
                  <img
                    src={movie.poster}
                    alt={movie.title}
                    className="w-full h-64 object-cover"
                  />
                  <div className="p-4">
                    <h3 className="text-white font-bold text-lg mb-2 line-clamp-1">{movie.title}</h3>
                    <p className="text-gray-400 text-sm mb-3 line-clamp-2">{movie.synopsis}</p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(movie)}
                        className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800"
                      >
                        <Edit size={16} className="mr-1" />
                        Düzenle
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(movie._id)}
                        className="border-red-700 text-red-500 hover:bg-red-900/20"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">
                {editingMovie ? 'Film Düzenle' : 'Yeni Film Ekle'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Film Adı</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  required
                />
              </div>

              <div>
                <Label htmlFor="original_title">Orijinal Adı (İngilizce/Orjinal Dil)</Label>
                <Input
                  id="original_title"
                  value={formData.original_title}
                  onChange={(e) => setFormData({ ...formData, original_title: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  placeholder="Örn: The Godfather"
                />
              </div>

              <div>
                <Label htmlFor="poster">Poster URL (Film Afişi)</Label>
                <Input
                  id="poster"
                  value={formData.poster}
                  onChange={(e) => setFormData({ ...formData, poster: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  required
                />
              </div>

              <div>
                <Label htmlFor="header_image">Header Görsel URL (Slider için)</Label>
                <Input
                  id="header_image"
                  value={formData.header_image}
                  onChange={(e) => setFormData({ ...formData, header_image: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  placeholder="Anasayfa slider'ında gösterilecek geniş görsel"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="show_in_slider"
                  checked={formData.show_in_slider}
                  onChange={(e) => setFormData({ ...formData, show_in_slider: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="show_in_slider" className="cursor-pointer">
                  Anasayfa Slider'ında Göster
                </Label>
              </div>


              <div>
                <Label htmlFor="trailer">Trailer URL (YouTube)</Label>
                <Input
                  id="trailer"
                  value={formData.trailer}
                  onChange={(e) => setFormData({ ...formData, trailer: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                />
              </div>

              <div>
                <Label htmlFor="synopsis">Açıklama</Label>
                <Textarea
                  id="synopsis"
                  value={formData.synopsis}
                  onChange={(e) => setFormData({ ...formData, synopsis: e.target.value })}
                  className="bg-gray-800 border-gray-700 min-h-[100px]"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="duration">Süre (dakika)</Label>
                  <Input
                    id="duration"
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Film Derecelendirme ve Akıllı İşaretler (Birden fazla seçilebilir)</Label>
                <div className="space-y-3 bg-gray-800 border border-gray-700 rounded-md p-4 max-h-96 overflow-y-auto">
                  <p className="text-gray-400 text-sm mb-3">Seçilen işaretler film kartlarında ve detay sayfasında gösterilecektir.</p>
                  {smartRatingOptions.map((rating) => (
                    <label key={rating._id} className="flex items-start gap-3 text-gray-300 cursor-pointer hover:bg-gray-700/50 p-3 rounded transition-colors">
                      <input
                        type="checkbox"
                        checked={(formData.smartRatings || []).includes(rating._id)}
                        onChange={(e) => {
                          const currentRatings = formData.smartRatings || [];
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              smartRatings: [...currentRatings, rating._id]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              smartRatings: currentRatings.filter(r => r !== rating._id)
                            });
                          }
                        }}
                        className="w-5 h-5 rounded mt-0.5 flex-shrink-0"
                      />
                      <div className="flex items-center gap-3">
                        {rating.icon && (
                          <img 
                            src={rating.icon} 
                            alt={rating.label}
                            className="w-6 h-6 object-contain flex-shrink-0"
                          />
                        )}
                        <span className="text-sm leading-relaxed">{rating.label}</span>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-gray-500 text-xs mt-2">
                  💡 Seçtiğiniz işaretler film kartlarında sarı rozet olarak, film detay sayfasında ise tam metin olarak görüntülenecektir.
                </p>
              </div>

              <div>
                <Label htmlFor="genres">Türler (virgülle ayırın)</Label>
                <Input
                  id="genres"
                  value={formData.genres}
                  onChange={(e) => setFormData({ ...formData, genres: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  placeholder="Örn: Aksiyon, Macera, Gerilim"
                  required
                />
              </div>

              <div>
                <Label htmlFor="formats">Film Formatları</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                  {['2D', '3D', 'IMAX', 'Türkçe Dublaj', 'Alt Yazılı', 'Türkçe'].map((format) => (
                    <label key={format} className="flex items-center gap-2 text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.formats.includes(format)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, formats: [...formData.formats, format] });
                          } else {
                            setFormData({ ...formData, formats: formData.formats.filter(f => f !== format) });
                          }
                        }}
                        className="w-4 h-4 rounded border-gray-700 bg-gray-800 text-[#FDB714] focus:ring-[#FDB714]"
                      />
                      <span className="text-sm">{format}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="release_date">Vizyon Tarihi</Label>
                  <Input
                    id="release_date"
                    type="date"
                    value={formData.release_date}
                    onChange={(e) => setFormData({ ...formData, release_date: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="status">Durum</Label>
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white"
                  >
                    <option value="now_playing">Vizyonda</option>
                    <option value="upcoming">Yakında</option>
                    <option value="archived">Arşiv</option>
                  </select>
                </div>
              </div>

              {/* Film Detay Bilgileri */}
              <div className="border-t border-gray-700 pt-6 mt-6">
                <h3 className="text-lg font-semibold text-white mb-4">Film Detay Bilgileri (Opsiyonel)</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="director">Yönetmen</Label>
                      <Input
                        id="director"
                        value={formData.director}
                        onChange={(e) => setFormData({ ...formData, director: e.target.value })}
                        className="bg-gray-800 border-gray-700"
                        placeholder="Örn: Christopher Nolan"
                      />
                    </div>

                    <div>
                      <Label htmlFor="country">Ülke</Label>
                      <Input
                        id="country"
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        className="bg-gray-800 border-gray-700"
                        placeholder="Örn: ABD, İngiltere"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="cast">Oyuncular</Label>
                    <Input
                      id="cast"
                      value={formData.cast}
                      onChange={(e) => setFormData({ ...formData, cast: e.target.value })}
                      className="bg-gray-800 border-gray-700"
                      placeholder="Örn: Leonardo DiCaprio, Tom Hardy, Marion Cotillard"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="producer">Yapımcı</Label>
                      <Input
                        id="producer"
                        value={formData.producer}
                        onChange={(e) => setFormData({ ...formData, producer: e.target.value })}
                        className="bg-gray-800 border-gray-700"
                        placeholder="Örn: Warner Bros"
                      />
                    </div>

                    <div>
                      <Label htmlFor="distributor">Sinema Dağıtım</Label>
                      <Input
                        id="distributor"
                        value={formData.distributor}
                        onChange={(e) => setFormData({ ...formData, distributor: e.target.value })}
                        className="bg-gray-800 border-gray-700"
                        placeholder="Örn: UIP Filmcilik"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="importer">İthalatçı</Label>
                      <Input
                        id="importer"
                        value={formData.importer}
                        onChange={(e) => setFormData({ ...formData, importer: e.target.value })}
                        className="bg-gray-800 border-gray-700"
                        placeholder="Örn: Warner Bros"
                      />
                    </div>

                    <div>
                      <Label htmlFor="second_release_date">2. Vizyon Tarihi</Label>
                      <Input
                        id="second_release_date"
                        type="date"
                        value={formData.second_release_date}
                        onChange={(e) => setFormData({ ...formData, second_release_date: e.target.value })}
                        className="bg-gray-800 border-gray-700 text-white [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  className="flex-1 bg-[#FDB714] hover:bg-[#e5a510] text-black font-semibold"
                >
                  {editingMovie ? 'Güncelle' : 'Ekle'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="border-gray-700 text-gray-300 hover:bg-gray-800"
                >
                  İptal
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminMovies;
